// Public routes and landing pages (ADR-0036). One list feeds the router, the build-time pre-render
// and sitemap.xml, so a page cannot be in one and missing in another. Texts follow ADR-0031:
// every sentence is checked against what the product does (sources of each claim: the BL-104 report).
// Inline links in paragraphs, bullets, steps and FAQ answers: `[text](/path)` or `[text](https://…)`.
import { t, type MessageKey } from "./i18n";
import { PRICING_PUBLIC } from "./flags";
import { priceText } from "./price";
import { CLIENTS_WORD } from "./lib";

export type LandingSection = { h2: string; paragraphs?: string[]; bullets?: string[]; steps?: string[] };
export type LandingFaq = { q: string; a: string };
export interface LandingPage {
  /** Path without a trailing slash, e.g. "/mcp/appsflyer". */
  path: string;
  /** <title>, at most 60 characters. */
  title: string;
  /** Meta description, at most 155 characters. */
  description: string;
  kicker?: string;
  h1: string;
  lead: string;
  /** Source ids from guides.ts, for the brand marks. */
  sources: string[];
  /** Source id for the «Connect» button: /connections#<connectAnchor>. */
  connectAnchor?: string;
  sections: LandingSection[];
  faq?: LandingFaq[];
  /** Paths of other landing pages. */
  related: string[];
  /** Name in the BreadcrumbList. */
  breadcrumb: string;
  /** Date the facts were checked, shown on /vs pages. */
  checked?: string;
  table?: { caption: string; head: string[]; rows: string[][] };
  /** Small print at the very end of the page (e.g. a trademark notice on /vs pages). */
  footnote?: string;
  /** Path of the hub page this page belongs to (a «← …» link and a three-level BreadcrumbList). */
  parent?: string;
  /** Where «Install V-Kit» leads, when not /install (e.g. /install#mcp). */
  installHref?: string;
}

/** A piece of inline text: plain, or a link written as `[text](href)`. */
export type InlinePart = { text: string } | { text: string; href: string };

const LINK = /\[([^\]]+)\]\(([^)\s]+)\)/g;

/** Splits `[text](href)` links out of a string. */
export function inlineParts(s: string): InlinePart[] {
  const out: InlinePart[] = [];
  let last = 0;
  for (const m of s.matchAll(LINK)) {
    if (m.index! > last) out.push({ text: s.slice(last, m.index) });
    out.push({ text: m[1]!, href: m[2]! });
    last = m.index! + m[0].length;
  }
  if (last < s.length) out.push({ text: s.slice(last) });
  return out;
}

/** The text with links reduced to their words (JSON-LD, tests). */
export function plainText(s: string): string {
  return s.replace(LINK, "$1");
}

// ── Facts shared by several pages (each checked once: privacy.html §2, en.ts install.chatgpt*, ADR-0032) ──

const STEP_INSTALL =
  "[Install V-Kit](/install): in Claude, the plugin from claude.ai/customize/plugins; in ChatGPT, the V-Kit plugin from ChatGPT Plugins.";
const STEP_ASK = "Ask in a new chat. A good first message: “check v-marketing-kit status”.";

const FAQ_CHATGPT: LandingFaq = {
  q: "Does it work in ChatGPT?",
  a: "Yes. V-Kit is in the ChatGPT plugin directory, on any plan, Free included: open Plugins, find V-Kit, press Install plugin and sign in. The steps are on [Install](/install#chatgpt).",
};

const faqStored = (source: string): LandingFaq => ({
  q: "Is my data stored?",
  a: `No. Your ${source} numbers are fetched when you ask and passed to your assistant. The server keeps a short in-memory cache (about 15 minutes, per user) so identical requests are not repeated, and a result too large for the chat waits behind a download link for up to 24 hours, for you only. Details are in the [privacy policy](/privacy.html).`,
});

// «Several clients» row of the /vs tables (BL-194). V-Kit: ADR-0062 (up to MK_CLIENTS_MAX = 3, same price). The other
// services: their pages were checked on 2026-10-04 for other facts, and nothing about several clients was recorded then,
// so the cell says «not stated» with that date - not a claim that they lack it.
const CLIENTS_ROW_VKIT = `Up to ${CLIENTS_WORD} clients, included in the price: separate sign-ins and keys for each customer`;
const CLIENTS_ROW_NOT_STATED = "- (not stated on the pages we checked on 2026-10-04)";

const PRIVACY_BULLETS = [
  "Read-only, except the changes you confirm in chat (copying AppsFlyer partner-integration settings and turning on App Store Connect Analytics Reports).",
  "Tokens and keys are encrypted at rest (AES-256-GCM) and never shown back, logged or put into answers.",
  "Your numbers are fetched when you ask and not stored beyond a 15-minute in-memory cache; large results wait behind a download link for up to 24 hours. See the [privacy policy](/privacy.html).",
  "Disconnect a source, remove a key or delete your account on the site at any time.",
];

const PAGES: LandingPage[] = [
  // ── 1. App Store Connect ────────────────────────────────────────────────────────────────────
  {
    path: "/mcp/app-store-connect",
    title: "App Store Connect MCP for Claude & ChatGPT | V-Kit",
    description:
      "Ask Claude, Claude Code or ChatGPT about App Store Connect sales, subscriptions, analytics and reviews. Hosted: no local server, no .p8 on your laptop.",
    kicker: "App Store Connect · Claude · ChatGPT",
    h1: "App Store Connect MCP for Claude and ChatGPT",
    lead: "Sales, subscriptions, App Store analytics and reviews of your own apps, answered in plain words. V-Kit is hosted, so there is nothing to run on your computer.",
    sources: ["app_store_connect"],
    connectAnchor: "app_store_connect",
    sections: [
      {
        h2: "What you can ask",
        bullets: [
          "“Installs, redownloads and proceeds by country last week.” Apple’s own Sales and Trends, up to 92 days by day or by month; proceeds stay in each currency.",
          "“How many active subscriptions and trials did we have each day this month?” and “How many trials converted, how many people cancelled or got a refund?”",
          "“What does the App Store funnel look like?” Impressions, product page views, downloads and conversion, by source; also sessions, installs and deletions, and crashes.",
          "“Show this week’s one- and two-star reviews in the US and whether we replied.”",
          "“Which version is live and which one is in review?” Versions with their states, and the setup of Product Page Optimization experiments (Apple’s API gives no test results).",
        ],
      },
      {
        h2: "Analytics without a local server",
        paragraphs: [
          "There is no process to run on your machine and no .p8 file to keep next to one. You save an App Store Connect team key once on [Connections](/connections#app-store-connect); it is stored encrypted on the server. For every request the server signs a fresh token (JWT, ES256, valid for 15 minutes) and calls Apple on your behalf.",
          "The saved key works wherever you use V-Kit: the Claude plugin, the custom connector on claude.ai, Claude Code and ChatGPT.",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          STEP_INSTALL,
          "In App Store Connect open Users and Access → Integrations → Team Keys, create a key and download its .p8 file (Apple allows it once). On [Connections](/connections#app-store-connect) enter the Issuer ID, the Key ID and the .p8, plus the vendor number for sales and subscriptions.",
          "Ask: “installs and proceeds last week”.",
        ],
      },
      {
        h2: "What it reads, and the one change it can make",
        bullets: [
          "Modes: apps, sales, subscriptions, analytics, reviews, versions and experiments.",
          "Sales and subscriptions need the vendor number; apps, reviews and analytics work without it.",
          "Key roles: Sales and Reports or Finance for sales, subscriptions and analytics; App Manager, Developer or Marketing for apps, reviews, versions and experiments. Admin covers everything.",
          "The one change: turning on Analytics Reports for an app, which Apple needs before it produces analytics. The assistant first shows what will be created and runs it only after your explicit yes; it needs an Admin key, and data appears in 1–2 days.",
          "Not done: replying to reviews, or changing apps, versions, prices or experiments.",
        ],
      },
      {
        h2: "Limits",
        bullets: [
          "Sales reports are Apple’s daily reports in Pacific time, ready the next day (about 08:00 Pacific).",
          "Analytics stays empty for an app until Analytics Reports is turned on.",
          "This source is your own team’s apps. Ratings, reviews and top charts of any app in the public App Store are a separate source that needs no key - see [mobile app marketing](/mobile-app-marketing-mcp).",
        ],
      },
    ],
    faq: [
      {
        q: "Do I need to run an App Store Connect MCP server myself?",
        a: "No. V-Kit is hosted at marketing-kit.app; your assistant connects to it over the internet and you sign in with Google or Meta. You install the plugin or the connector, nothing else.",
      },
      {
        q: "Where is my .p8 key kept?",
        a: "Encrypted (AES-256-GCM) on the V-Kit server, used only to sign requests to Apple, and never shown back, logged or put into answers. Remove it on [Connections](/connections#app-store-connect) at any time, and revoke it in App Store Connect when you stop using V-Kit.",
      },
      {
        q: "Can it change anything in App Store Connect?",
        a: "One thing: it can turn on Analytics Reports for an app, after showing you the request and getting your explicit yes. It does not reply to reviews or change apps, versions or prices.",
      },
      FAQ_CHATGPT,
      faqStored("App Store Connect"),
    ],
    related: ["/mcp/google-play", "/mcp/apple-ads", "/mcp/appsflyer", "/mcp/revenuecat", "/mobile-app-marketing-mcp", "/vs/supermetrics"],
    breadcrumb: "App Store Connect",
  },

  // ── 1b. Google Play (BL-166; facts: packs/google-play, ADR-0057) ──────────────────────────
  {
    path: "/mcp/google-play",
    title: "Google Play Console MCP for Claude & ChatGPT | V-Kit",
    description:
      "Ask Claude, Claude Code or ChatGPT about Google Play installs, sales, earnings, subscriptions, reviews, refunds and crash rates. Hosted, read-only.",
    kicker: "Google Play · Claude · ChatGPT",
    h1: "Google Play Console MCP for Claude and ChatGPT",
    lead: "Installs, estimated sales, earnings, subscription counts, reviews and crash rates of your own Android apps, answered in plain words. V-Kit is hosted, so there is nothing to run on your computer.",
    sources: ["google_play"],
    connectAnchor: "google_play",
    sections: [
      {
        h2: "What you can ask",
        bullets: [
          "“Installs and uninstalls of my Android app last month, by country.” Also by device, app version, OS version, carrier and language.",
          "“What did we sell in Germany last month, and what did we earn after Google’s fee?” Estimated sales and earnings, totalled per currency.",
          "“How many subscribers did we gain and lose this month?” Subscription counts: new, canceled and active.",
          "“Show the reviews of the last 7 days with one or two stars.”",
          "“Which purchases were refunded in the last 30 days?” Refunds and voided purchases.",
          "“What are our crash and ANR rates this week?” Android vitals, daily or hourly.",
        ],
      },
      {
        h2: "Play Console data without a local server",
        paragraphs: [
          "There is no process to run on your machine and no service-account file to keep. You press Connect Google Play once on [Connections](/connections#google-play), choose the Google account that has access to your Play Console - it may differ from the one you use for GA4 or Google Ads - and paste the address of your reports bucket from Play Console → Download reports. V-Kit reads with that account’s own permissions.",
          "The connection works wherever you use V-Kit: the Claude plugin, the custom connector on claude.ai, Claude Code and ChatGPT.",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          STEP_INSTALL,
          "On [Connections](/connections#google-play) press Connect Google Play, pick the Google account that has access to your Play Console and keep every box checked.",
          "In Play Console open Download reports, press Copy Cloud Storage URI and paste it into Reports bucket on Connections.",
          "Ask: “installs of my Android app last month”.",
        ],
      },
      {
        h2: "What it reads",
        bullets: [
          "Modes: apps, installs, sales, earnings, subscriptions, reviews, refunds and vitals. All of them only read.",
          "Play Console permissions of the Google account, set to Global by the owner of the Play Console account: “View app information and download bulk reports (read-only)” for installs, vitals and reviews of past months; “View financial data” for sales, earnings and refunds. Subscription counts need the first and may also need the second; Google does not say which.",
          "Not done: replying to reviews, changing apps, prices or listings, or anything else that writes.",
        ],
      },
      {
        h2: "Limits",
        bullets: [
          "Play reports arrive 3–7 days late, so yesterday’s numbers are not there yet; earnings arrive around the 5th of the next month.",
          "Google’s API returns reviews of the last 7 days; older months come from the monthly reviews report. Refunds cover the last 30 days.",
          "Sales are estimates, not accounting figures. Sales and earnings are totalled per currency; currencies are never added together.",
          "This source is your own Android apps. For attribution use [AppsFlyer](/mcp/appsflyer), for subscription billing across stores [RevenueCat](/mcp/revenuecat), and for the Apple side [App Store Connect](/mcp/app-store-connect).",
        ],
      },
    ],
    faq: [
      {
        q: "Do I need to run a Google Play MCP server myself?",
        a: "No. V-Kit is hosted at marketing-kit.app; your assistant connects to it over the internet. You install the plugin or the connector and press Connect Google Play, nothing else.",
      },
      {
        q: "Can it change anything in Play Console?",
        a: "No. Every Google Play tool only reads. It does not reply to reviews or change apps, prices or listings.",
      },
      {
        q: "Which Play Console permissions does it need?",
        a: "Those of the Google account you connect: “View app information and download bulk reports (read-only)” for installs, vitals and reviews of past months, and “View financial data” for sales, earnings and refunds. Subscription counts may need either. If one is missing, the answer names it and the owner of the Play Console account can grant it under Users and permissions.",
      },
      {
        q: "Where do I find the reports bucket?",
        a: "In Play Console open Download reports and press Copy Cloud Storage URI; paste it into Reports bucket on [Connections](/connections#google-play). Nothing can find it automatically. It is a bucket name, not a secret.",
      },
      FAQ_CHATGPT,
      faqStored("Google Play"),
    ],
    related: ["/mcp/app-store-connect", "/mcp/appsflyer", "/mcp/revenuecat", "/mobile-app-marketing-mcp"],
    breadcrumb: "Google Play",
  },

  // ── 2. AppsFlyer ────────────────────────────────────────────────────────────────────────────
  {
    path: "/mcp/appsflyer",
    title: "AppsFlyer MCP - Installs & Events in Claude | V-Kit",
    description:
      "Ask Claude, Claude Code or ChatGPT about AppsFlyer installs and in-app events by media source, campaign and geo; find purchases counted twice.",
    kicker: "AppsFlyer · Claude · ChatGPT",
    h1: "AppsFlyer MCP for Claude and ChatGPT",
    lead: "Installs and in-app events from your AppsFlyer reports, by media source, campaign, geo and day, in plain words and next to your ad spend and subscriptions.",
    sources: ["appsflyer"],
    connectAnchor: "appsflyer",
    sections: [
      {
        h2: "What you can ask",
        bullets: [
          "“Installs by media source and campaign last week.” Aggregate reports: daily, partners, partners by date, geo and geo by date.",
          "“Which campaigns brought purchases in Germany?” In-app events by media source, campaign, ad set and geo.",
          "“Export last week’s non-organic installs row by row.” Raw installs and in-app events, if your plan has Raw Data. Device IDs, IPs and customer user IDs are removed unless you ask for them.",
          "“Are our purchases counted twice?” See the next section.",
        ],
      },
      {
        h2: "Purchase duplicates: SDK and S2S",
        paragraphs: [
          "When the app logs a purchase from the SDK and RevenueCat sends the same purchase server-to-server, AppsFlyer can count it twice. The purchase_dedup mode reads raw in-app events of one app for up to 31 days, splits them by Event Source (SDK or S2S) and receipt validation, and finds groups of duplicates.",
          "It returns counts only, no IDs, and needs the Raw Data module. Claude then compares the result with [RevenueCat](/mcp/revenuecat) charts for the same days and suggests one source per event: the first purchase and the trial start from the app, conversions, renewals, cancellations and refunds from RevenueCat. V-Kit does not change your AppsFlyer or RevenueCat settings or your app’s code.",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          STEP_INSTALL,
          "In AppsFlyer open the profile menu → Security Center → AppsFlyer tokens → Manage your AppsFlyer tokens, copy the API token V2 (an account admin sees the tokens) and paste it on [Connections](/connections#appsflyer). In Claude Desktop with the V-Kit extension you can keep it in the extension settings instead.",
          STEP_ASK,
        ],
      },
      {
        h2: "What it reads, and what it can change",
        bullets: [
          "Reads: your apps, aggregate and raw Pull API reports, and purchase duplicates.",
          "The only change, previewed before → after and applied only after your explicit yes: copying partner integration settings from one app to another.",
        ],
      },
      {
        h2: "Limits",
        bullets: [
          "Raw reports and purchase duplicates need the Raw Data module in your AppsFlyer plan; aggregate reports work without it.",
          "Tokens issued before 2026-03-10 no longer work; create a new V2 token.",
        ],
      },
    ],
    faq: [
      {
        q: "Is the AppsFlyer connection read-only?",
        a: "Reports are read-only. The only change is copying partner integration settings from one app to another; it is shown before → after and applied only after your explicit yes in chat. V-Kit does not create or edit OneLink links.",
      },
      {
        q: "Where is my AppsFlyer token kept?",
        a: "Saved on [Connections](/connections#appsflyer), it is encrypted (AES-256-GCM) and only its last characters are ever shown. Kept in the Claude Desktop extension, it stays on your computer and is sent with each request; a token in the extension takes priority.",
      },
      {
        q: "How does it find purchases counted twice?",
        a: "The purchase_dedup mode matches purchases by order ID when both sides have it, otherwise by the same AppsFlyer ID or customer user ID within 60 minutes by default, and reports the groups as counts: app and server, app and app, server and server.",
      },
      FAQ_CHATGPT,
      faqStored("AppsFlyer"),
    ],
    related: ["/mcp/revenuecat", "/mcp/app-store-connect", "/mcp/google-play", "/mcp/apple-ads", "/mobile-app-marketing-mcp", "/mcp/meta-ads", "/vs/supermetrics", "/vs"],
    breadcrumb: "AppsFlyer",
  },

  // ── 3. RevenueCat ───────────────────────────────────────────────────────────────────────────
  {
    path: "/mcp/revenuecat",
    title: "RevenueCat in Claude & ChatGPT: MRR, Trials | V-Kit",
    description:
      "Ask Claude, Claude Code or ChatGPT about RevenueCat MRR, trials, churn and subscriptions, and match them to installs and spend. Read-only V2 key.",
    kicker: "RevenueCat · Claude · ChatGPT",
    h1: "RevenueCat MCP for Claude and ChatGPT",
    lead: "MRR, active subscriptions, trials, churn and revenue from RevenueCat, in plain words, and next to installs and ad spend when you ask.",
    sources: ["revenuecat"],
    connectAnchor: "revenuecat",
    sections: [
      {
        h2: "What you can ask",
        bullets: [
          "“What is our MRR, and how many active trials and subscriptions do we have?” The overview, with revenue and new customers of the last 28 days.",
          "“Trial conversion by month this year.” RevenueCat charts by day, week or month, with the segments and filters RevenueCat offers for each chart.",
          "“Churn and refunds over the last 90 days, by week.” Also MRR, revenue, actives, trials, conversion to paying, LTV and cohorts.",
          "“Revenue last quarter: gross, after taxes and proceeds.”",
          "“Which products does the paywall offer right now?” Offerings, packages, products and entitlements.",
          "“What is the subscription status of app user 123?” One customer by app user ID; email and other personal data stay hidden unless you ask.",
        ],
      },
      {
        h2: "Next to installs and spend",
        paragraphs: [
          "Ask Claude to put RevenueCat next to [AppsFlyer](/mcp/appsflyer) installs, [App Store Connect](/mcp/app-store-connect) downloads or ad spend from Meta, Google Ads and TikTok for the same days, for example new trials per day against spend. V-Kit reads each source; the assistant does the arithmetic.",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          STEP_INSTALL,
          "In RevenueCat open Project settings → API keys → New secret API key, choose version V2 and give it Read on Charts & Metrics, Customer Information and Project Configuration. Paste it on [Connections](/connections#revenuecat), or keep it in the Claude Desktop extension settings.",
          "Ask: “what is our MRR” or “trial conversion last month”.",
        ],
      },
      {
        h2: "Access and limits",
        bullets: [
          "Read-only: V-Kit calls the reading endpoints of RevenueCat REST API v2, and a key with Read permissions cannot change anything.",
          "Public SDK keys (appl_, goog_) and v1 keys are rejected by RevenueCat; create a secret V2 key.",
          "A V2 key belongs to one project.",
          "RevenueCat allows 25 chart and metric requests per minute per key.",
        ],
      },
    ],
    faq: [
      {
        q: "Is it read-only?",
        a: "Yes. V-Kit only reads RevenueCat: metrics, charts, products, offerings and, when you ask about one customer, that customer’s subscriptions. A key with Read permissions cannot change anything anyway.",
      },
      {
        q: "Which RevenueCat key do I need?",
        a: "A secret API key, version V2, with Read on Charts & Metrics, Customer Information and Project Configuration and no access to anything else. Public SDK keys and v1 keys do not work.",
      },
      {
        q: "Where is the key kept?",
        a: "Saved on [Connections](/connections#revenuecat), it is encrypted (AES-256-GCM) and never shown back except its last characters. In the Claude Desktop extension it stays on your computer and is used for each request only.",
      },
      FAQ_CHATGPT,
      faqStored("RevenueCat"),
    ],
    related: ["/mcp/appsflyer", "/mcp/app-store-connect", "/mcp/google-play", "/mobile-app-marketing-mcp", "/vs/supermetrics"],
    breadcrumb: "RevenueCat",
  },

  // ── 4. Mobile app marketing ─────────────────────────────────────────────────────────────────
  {
    path: "/mobile-app-marketing-mcp",
    title: "Mobile App Marketing MCP: Spend, Installs, Revenue | V-Kit",
    description:
      "One connector for Meta, Google Ads, TikTok, Apple Ads, AppsFlyer, RevenueCat, App Store Connect: spend, installs, trials in Claude, Claude Code or ChatGPT.",
    kicker: "Mobile apps · Claude · ChatGPT",
    h1: "One MCP connector for your mobile app marketing stack",
    lead: "Ad spend, installs, trials and revenue live in different consoles. V-Kit connects them to Claude or ChatGPT, so one question can read all of them.",
    sources: ["meta", "google_ads", "tiktok", "apple_ads", "appsflyer", "revenuecat", "app_store_connect", "google_play", "apple"],
    sections: [
      {
        h2: "The stack, step by step",
        bullets: [
          "Spend: [Meta Ads](/mcp/meta-ads), [Google Ads](/mcp/google-ads) and [TikTok Ads](/mcp/tiktok-ads) - spend, impressions, clicks and conversions by campaign; [Apple Ads](/mcp/apple-ads) - spend, taps and installs of App Store search ads by campaign, keyword and search term.",
          "Installs: [AppsFlyer](/mcp/appsflyer) - installs and in-app events by media source and campaign; [App Store Connect](/mcp/app-store-connect) - Apple’s own downloads by country and the App Store funnel; [Google Play](/mcp/google-play) - installs and uninstalls of your Android apps.",
          "Trials and revenue: [RevenueCat](/mcp/revenuecat) - MRR, trials, conversion and churn; App Store Connect - sales, proceeds and subscription events; Google Play - estimated sales, earnings and subscription counts.",
          "The store page: public App Store data - version, rating, reviews and top charts by country for any app, yours or a competitor’s, with no key.",
          "Around it: [GA4](/mcp/google-analytics-4), [Search Console](/mcp/search-console), [Instagram](/mcp/instagram) and [Facebook Pages](/mcp/facebook-pages).",
          `Apps of several customers: keep each customer’s sign-ins and keys in its own client, up to ${CLIENTS_WORD} in one account and one price - see [V-Kit for agencies](/for-agencies).`,
        ],
      },
      {
        h2: "Questions that cross sources",
        paragraphs: ["V-Kit reads each source as it reports; the assistant combines them. Ask for things like:"],
        bullets: [
          "“Cost per install last week: Meta and TikTok spend divided by AppsFlyer installs for the same media sources.”",
          "“Trial conversion this month from RevenueCat, next to new downloads from App Store Connect.”",
          "“Spend across Meta, Google Ads and TikTok against RevenueCat revenue, per day for the last 30 days.”",
          "“Do AppsFlyer and RevenueCat count the same purchases twice?”",
          "“How did our rating and latest reviews change after the last release?”",
        ],
      },
      {
        h2: "Honest about the numbers",
        paragraphs: [
          "Each platform counts its own way: ad platforms by their attribution, AppsFlyer by its attribution, RevenueCat by store transactions, Apple by Pacific-time days. V-Kit does not merge them into one model. A ratio built from two sources (spend from one, revenue from another) is blended across channels, not attributed to a campaign; ask the assistant to show each source’s number next to the result.",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          STEP_INSTALL,
          "On [Connections](/connections) authorize Meta, Google and TikTok in their own dialogs, connect Google Play with its own Connect Google Play and paste its reports bucket, connect Apple Ads with its own Connect Apple Ads, and save the keys for AppsFlyer, RevenueCat and App Store Connect. Public App Store data needs nothing.",
          STEP_ASK,
        ],
      },
      { h2: "Access and privacy", bullets: PRIVACY_BULLETS },
    ],
    faq: [
      {
        q: "Can it calculate CPI and ROAS?",
        a: "The assistant can divide spend by installs or revenue by spend for you. Spend comes from the ad platforms, installs from AppsFlyer or App Store Connect, revenue from RevenueCat or App Store Connect. Unless both numbers come from AppsFlyer’s in-app events by media source, such a ratio is blended across channels.",
      },
      {
        q: "Which sources need a key?",
        a: "Google, Meta and TikTok are authorized in their own sign-in dialogs. Google Play has its own Connect Google Play, a separate Google sign-in, plus the reports bucket you paste. Apple Ads has its own Connect Apple Ads, an Apple sign-in, with no key. AppsFlyer, RevenueCat and App Store Connect use keys you save on [Connections](/connections). Public App Store data needs nothing.",
      },
      FAQ_CHATGPT,
      faqStored("marketing"),
    ],
    related: ["/mcp/app-store-connect", "/mcp/google-play", "/mcp/apple-ads", "/mcp/appsflyer", "/mcp/revenuecat", "/mcp/meta-ads", "/mcp/any-app", "/for-agencies", "/vs/supermetrics", "/vs"],
    breadcrumb: "Mobile app marketing",
  },

  // ── 4a. Any MCP app (owner 2026-11-02: «Other apps (MCP)» on /install, an SEO page for it, DeepSeek, the SDK form).
  //   Sources, checked 2026-11-02: Cursor - cursor.com/docs/context/mcp (Streamable HTTP by URL, OAuth with PKCE);
  //   Gemini CLI - geminicli.com/docs/tools/mcp-server (remote HTTP, OAuth discovered, dynamic client registration);
  //   VS Code - code.visualstudio.com/docs/copilot/customization/mcp-servers (a server from a URL, sign-in when asked);
  //   Le Chat - Mistral connectors, custom MCP connector by URL, OAuth window (Qonto, Civic integration docs);
  //   Cherry Studio - Settings → MCP → Streamable HTTP, OAuth on save (Tolstoy docs), DeepSeek models built in;
  //   DeepSeek's own chat app - no MCP connectors found (search 2026-11-02). Our side: /mcp is Streamable HTTP with
  //   OAuth 2.1, DCR and client metadata documents (ADR-0019, ADR-0061). SDK: not built - the page asks, promises nothing.
  {
    path: "/mcp/any-app",
    title: "Marketing MCP Server for Cursor, Gemini CLI, Le Chat | V-Kit",
    description:
      "Meta, Google Ads, GA4, AppsFlyer, RevenueCat, App Store Connect in Cursor, VS Code, Gemini CLI, Le Chat, Claude Code or any MCP app. Hosted, no keys.",
    kicker: "Any MCP app · Cursor · VS Code · Gemini CLI · Le Chat",
    h1: "A hosted marketing MCP server for any AI app",
    lead: "One address - https://marketing-kit.app/mcp - brings your ad, app and subscription numbers to every app that speaks MCP. Sign in with Google or Meta: no keys to copy, nothing to run on your computer.",
    sources: ["meta", "google_ads", "tiktok", "apple_ads", "ga4", "search_console", "appsflyer", "revenuecat", "app_store_connect", "google_play", "apple"],
    installHref: "/install#mcp",
    sections: [
      {
        h2: "Apps it works with",
        paragraphs: [
          "Any app that adds a remote MCP server by URL (Streamable HTTP) and signs in with OAuth. These say so in their own documentation, checked on 2026-11-02:",
        ],
        bullets: [
          "Claude - the V-Kit plugin, with the know-how for each source; Claude Code - the same plugin from the terminal. See [Install](/install#plugin).",
          "ChatGPT - the V-Kit plugin from ChatGPT Plugins, any plan. See [Install](/install#chatgpt).",
          "Cursor - add the server by its URL; Cursor signs in with OAuth.",
          "VS Code with GitHub Copilot - add an MCP server from a URL (HTTP) and sign in when it asks.",
          "Gemini CLI - a remote server by URL; the CLI finds the sign-in and registers itself.",
          "Mistral Le Chat - Connectors → a custom MCP connector by URL; the sign-in opens in a window.",
          "Cherry Studio - Settings → MCP → Streamable HTTP; it signs in when you save the server.",
        ],
      },
      {
        h2: "DeepSeek and other models",
        paragraphs: [
          "DeepSeek’s own chat app does not connect MCP servers (checked on 2026-11-02). To ask a DeepSeek model about your numbers, use it inside an MCP app that offers DeepSeek models - Cherry Studio, for example - and add V-Kit there. The same works for other models such an app supports: V-Kit answers whichever model calls it.",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          "In your app, add a remote MCP server (Streamable HTTP) with the address https://marketing-kit.app/mcp and the name v-marketing-kit. Leave the OAuth client ID and secret empty - the app registers itself.",
          "Press Connect (or Authenticate), sign in with Google or Meta and press Allow. The sign-in only identifies you.",
          "On [Connections](/connections) authorize Meta, Google and TikTok in their own dialogs, connect Google Play and Apple Ads with their own buttons, and save the keys for AppsFlyer, RevenueCat and App Store Connect.",
          STEP_ASK,
        ],
      },
      {
        // The same facts as install.auth* in en.ts (server/src/mcp/routes.ts, oauth/routes.ts, oauth/store.ts).
        h2: "How the OAuth sign-in works",
        bullets: [
          "Your app calls https://marketing-kit.app/mcp and gets “sign in first” with a link to V-Kit’s OAuth settings, so it finds the sign-in page by itself.",
          "The app registers itself - dynamic client registration or a client metadata URL - so there is no client ID or secret to paste.",
          "A browser window opens marketing-kit.app: sign in with Google or Meta, check the name of the app asking for access, press Allow.",
          "The app receives a token for your account only (OAuth 2.1 with PKCE). It works for an hour and the app renews it by itself; after 90 days without use, sign in again. Your passwords and source keys never reach the app.",
          "Cut an app off any time under [Connections → Connected assistants](/connections#assistants).",
          "An app that only accepts a fixed token in a header can’t connect: V-Kit gives MCP apps no static API keys, only this sign-in.",
        ],
      },
      {
        h2: "What you can ask",
        bullets: [
          "“Spend, clicks and conversions by campaign in Meta and Google Ads last week.”",
          "“Installs by media source in AppsFlyer for the last 7 days.”",
          "“MRR, trials and churn in RevenueCat this month.”",
          "“Organic search queries that grew most in Search Console.”",
          "“Rating and latest reviews of our app in the App Store.”",
        ],
        paragraphs: [
          "Without the plugin there is no V-Kit know-how in the app, so the model relies on the tool descriptions alone and answers are a little rougher than in Claude or ChatGPT with the plugin.",
        ],
      },
      {
        h2: "V-Kit in your own app or agent",
        paragraphs: [
          "Building a product, an internal agent or an automation on top of marketing data? We are preparing an SDK: the same sources, the same sign-in and read-only access, called from your own code instead of a chat.",
          "[Request SDK access](/sdk) - tell us what you are building, which sources you need and in which language, and the developer will write back.",
        ],
      },
      { h2: "Access and privacy", bullets: PRIVACY_BULLETS },
    ],
    faq: [
      {
        q: "Do I need an API key for V-Kit?",
        a: "No. MCP apps sign in to V-Kit with OAuth - with Google or Meta. Only AppsFlyer, RevenueCat and App Store Connect need their own keys, and those are saved once on [Connections](/connections), never in the app or the chat.",
      },
      {
        q: "What is the server address?",
        a: "https://marketing-kit.app/mcp - Streamable HTTP, OAuth 2.1 with dynamic client registration. The same address serves the Claude plugin.",
      },
      {
        q: "Can I use V-Kit with DeepSeek?",
        a: "Not in DeepSeek’s own chat app: it does not connect MCP servers. Use a DeepSeek model in an MCP app that offers it, such as Cherry Studio, and add V-Kit there.",
      },
      {
        q: "Is there an SDK or an API for my own product?",
        a: "An SDK is in preparation. [Request access](/sdk) and tell us what you are building.",
      },
      FAQ_CHATGPT,
      faqStored("marketing"),
    ],
    related: ["/mobile-app-marketing-mcp", "/mcp/google-ads", "/mcp/meta-ads", "/mcp/appsflyer", "/mcp/revenuecat", "/mcp/app-store-connect", "/for-agencies", "/vs"],
    breadcrumb: "Any MCP app",
  },

  // ── 4b. For agencies and freelancers: clients (BL-194). Sources of each claim, by sentence group:
  //   clients = named groups of connections and keys, up to MK_CLIENTS_MAX (3) per account, same price, no bigger tier,
  //   more → /clients/more (ADR-0062; server clients/store.ts, 409 clients_limit - server/test/clients-api.test.ts);
  //   the client named in the question, several clients and no name → 409 client_required with the list, never guessed,
  //   one client → never asked (ADR-0062; server clients/resolve.ts - server/test/clients-data.test.ts);
  //   the model asks once and keeps the choice for the chat (ADR-0062 «The model's rule»; BL-193 instructions);
  //   Add client → name → Continue, tabs from the second client, rename, delete disconnects and removes keys, the last
  //   one cannot be deleted, OAuth carries the client (ADR-0062 «The site»; web/test/clients.test.tsx, clients-api.test.ts);
  //   the same Google or Meta account in two clients only by connecting it twice (ADR-0062);
  //   partner access: MCC detected automatically (ADR-0026, /mcp/google-ads), GA4 and Search Console show what the
  //   Google account can see (/mcp/google-analytics-4, /mcp/search-console), Meta ad accounts where the Facebook account
  //   has a role and business portfolio pages (/mcp/meta-ads, ADR-0017); keys: AppsFlyer token, RevenueCat V2 key of one
  //   project, App Store Connect team key, Google Play sign-in and bucket (/mcp/* pages, ADR-0057);
  //   no shared access for the customer and no higher limit promised (ADR-0062 «On the future»);
  //   the connect link (step 4, last FAQ): more than one client → «Authorize here» and «Copy link for another browser»,
  //   Google, Meta, TikTok, Google Play, Apple Ads, one use, 15 minutes, no V-Kit sign-in in that browser, the row turns to
  //   Connected by polling (ADR-0063; BL-197/BL-198, web/test/clients.test.tsx); inviting the customer - not promised.
  {
    path: "/for-agencies",
    title: "V-Kit for Agencies and Freelancers: Multi-Client MCP",
    description: `Keep up to ${CLIENTS_WORD} customers’ ad, analytics and app accounts and keys in one V-Kit: one plugin, one price. Ask Claude or ChatGPT “Acme: spend last week”.`,
    kicker: "Agencies · Freelancers · Claude · ChatGPT",
    h1: "V-Kit for agencies and freelancers: several clients, one plugin",
    lead: `Every customer has their own Google and Meta sign-ins and their own AppsFlyer, RevenueCat or App Store Connect keys. Clients keep up to ${CLIENTS_WORD} customers apart in one V-Kit account: one subscription, one plugin, and you name the client in the question.`,
    sources: ["google_ads", "meta", "ga4", "tiktok", "apple_ads", "appsflyer", "revenuecat", "app_store_connect", "google_play"],
    sections: [
      {
        h2: "Several customers, several sign-ins",
        paragraphs: [
          "An agency or a freelancer works for several customers, and each one’s Google Ads, GA4, Meta Business, AppsFlyer and RevenueCat live under different sign-ins and different keys. Without clients, the second Google account or the second AppsFlyer token needs a second V-Kit account, and switching customers means disconnecting and reconnecting the connector in Claude or ChatGPT.",
        ],
      },
      {
        h2: "How clients solve it",
        bullets: [
          "A client is a named group of connections and keys inside your account, one for each customer or project: its own Google, Meta, TikTok and Apple Ads sign-ins and its own AppsFlyer, RevenueCat, App Store Connect and Google Play keys.",
          `Up to ${CLIENTS_WORD} clients are included in the same subscription, with no per-client charge and no bigger plan.`,
          "One plugin or connector serves all of them, in Claude and in ChatGPT; you never install V-Kit twice.",
          "In the chat you name the client: “Acme: spend last week”. With several clients and no name, V-Kit asks which one you mean instead of guessing.",
        ],
      },
      {
        h2: "When partner access is enough",
        paragraphs: [
          "You may not need a client at all. If customers give your own account partner access, one sign-in already sees all of their accounts, and the accounts mode of each source lists them:",
        ],
        bullets: [
          "[Google Ads](/mcp/google-ads): accounts under your manager account (MCC) are found automatically.",
          "[GA4](/mcp/google-analytics-4) and [Search Console](/mcp/search-console): properties the customer shared with your Google account.",
          "[Meta Ads](/mcp/meta-ads): ad accounts where your Facebook account has a role, for example through your Business Manager; [Facebook Pages](/mcp/facebook-pages) and [Instagram](/mcp/instagram) of your business portfolio.",
        ],
      },
      {
        h2: "When you need clients",
        bullets: [
          "Key-based sources: an [AppsFlyer](/mcp/appsflyer) token, a [RevenueCat](/mcp/revenuecat) V2 key (one key covers one project), an [App Store Connect](/mcp/app-store-connect) team key and the reports bucket of a [Google Play](/mcp/google-play) developer account each belong to one customer, and one of each fits in a client.",
          "A customer who does not give partner access and lets you use their own Google, Meta, TikTok or Apple Ads sign-in instead.",
          "The same Google or Meta account in two clients is possible: connect it in each of them.",
        ],
      },
      {
        h2: "How to set it up",
        steps: [
          STEP_INSTALL,
          "Sign in and open [Connections](/connections). What you connected so far stays in your first client, Main; you can rename it.",
          "Press Add client, type the customer’s name (for example Acme) and press Continue. Connect that customer’s sources in the cards of the new client: authorize Google, Meta, TikTok and Apple Ads with the account that has access, and save their keys.",
          "If that account is signed in somewhere else (another Chrome profile, another browser or a colleague’s computer), press Copy link for another browser next to Authorize here and open the link there: choose the account and press Allow. The link works once and for 15 minutes, and that browser does not sign in to V-Kit.",
          "Ask with the name: “Acme: spend last week”. The [Setup guide](/guide#clients) has the details.",
        ],
      },
      {
        h2: "What clients are not",
        bullets: [
          "Not team access: your customers do not sign in to your V-Kit. You share the answers yourself.",
          "Not a shared connection: each client has its own sign-ins and keys, and deleting a client disconnects its sources and removes its keys. The last client cannot be deleted.",
          `Not unlimited: up to ${CLIENTS_WORD} per account. Need more? Tell us on [the request page](/clients/more): we will set it up for you, the price stays the same.`,
        ],
      },
      { h2: "Access and privacy", bullets: PRIVACY_BULLETS },
    ],
    faq: [
      {
        q: "Do clients cost extra?",
        a: `No. Up to ${CLIENTS_WORD} clients are included in the one plan, with no per-client charge and no bigger plan. ${priceText("long")}`,
      },
      {
        q: `What if I need more than ${CLIENTS_WORD} clients?`,
        a: "Tell us how many and why on [the request page](/clients/more). There is no bigger plan to buy and the price stays the same; the developer reads every request.",
      },
      {
        q: "Are my clients’ data kept apart?",
        a: "Yes. Each client has its own connections and keys, and a question about one client reads only that client’s sources. With several clients, a request that does not name a client gets the list of clients and no data: V-Kit never guesses. The numbers themselves are fetched when you ask and not stored ([privacy policy](/privacy.html)).",
      },
      {
        q: "How do I switch clients in a chat?",
        a: "Name the other client: “Bolt: same for last month”. The assistant passes the client with every request and keeps your choice for the chat; with several clients and no name it asks once which one you mean. An account with one client is never asked.",
      },
      {
        q: "My customer’s Google account is signed in to another browser - how do I connect it?",
        a: "On [Connections](/connections), in that customer’s client, press “Copy link for another browser” next to “Authorize here” (the two buttons appear once you have more than one client). Open the link where the account is signed in - another Chrome profile, another browser or a colleague’s computer - choose the account and press Allow. The link works once and for 15 minutes, and that browser does not sign in to V-Kit; the row on Connections turns to Connected by itself. The same works for Meta, TikTok, Google Play and Apple Ads.",
      },
    ],
    related: ["/mobile-app-marketing-mcp", "/mcp/google-ads", "/mcp/meta-ads", "/mcp/appsflyer", "/mcp/revenuecat", "/vs"],
    breadcrumb: "For agencies",
  },

  // ── 5. GA4 ──────────────────────────────────────────────────────────────────────────────────
  {
    path: "/mcp/google-analytics-4",
    title: "GA4 MCP Connector for Claude & ChatGPT | V-Kit",
    description:
      "Connect Google Analytics 4 to Claude, Claude Code or ChatGPT with Google sign-in. Hosted, read-only: no local server, no pipx, no own Cloud project.",
    kicker: "Google Analytics 4 · Claude · ChatGPT",
    h1: "GA4 MCP connector for Claude and ChatGPT",
    lead: "Users, sessions and events from your Google Analytics 4 properties, answered in plain words. You sign in with Google; there is nothing to install on your computer.",
    sources: ["ga4"],
    connectAnchor: "ga4",
    sections: [
      {
        h2: "What you can ask",
        bullets: [
          "“Active users and sessions by day for the last 28 days, by platform.”",
          "“How many users reached the paywall event this week, and how many started a trial?”",
          "“Top landing pages by sessions last month, by country.”",
          "“Who is on the site or in the app right now?” Realtime, the last 30 minutes.",
          "“Which custom events and user properties does this property have?” Metadata, including your custom dimensions and metrics.",
        ],
      },
      {
        h2: "Hosted, not a local server",
        paragraphs: [
          "Unlike running a local GA4 MCP server, there is no package to install, no Google Cloud project to create and no credentials file on your machine. You authorize Google once on [Connections](/connections#ga4); the token is stored encrypted on the server, which calls the GA4 Data and Admin APIs for you.",
          "The same sign-in covers [Google Ads](/mcp/google-ads) and [Search Console](/mcp/search-console).",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          STEP_INSTALL,
          "On [Connections](/connections#ga4) press Authorize with Google, pick the account that sees your GA4 property and keep every box checked.",
          STEP_ASK,
        ],
      },
      {
        h2: "What it reads",
        bullets: [
          "Scope analytics.readonly: reports, realtime and metadata. It cannot change properties, settings or users.",
          "You see the accounts and properties your Google account can see; Viewer access is enough.",
          "Answers say when GA4 applied sampling or thresholds.",
          "Results too large for the chat come as a CSV or JSON download link, valid for 24 hours and only for you.",
        ],
      },
      {
        h2: "Limits",
        bullets: [
          "Realtime has its own GA4 quota, separate from regular reports.",
          "If Google shows “Google hasn’t verified this app”, Google’s review of the Marketing Kit app is still pending: press Advanced → Go to marketing-kit.app → Continue.",
        ],
      },
    ],
    faq: [
      {
        q: "Do I need a Google Cloud project or API keys?",
        a: "No. V-Kit uses its own Google app; you only sign in with the Google account that has access to your GA4 property.",
      },
      {
        q: "Is it read-only?",
        a: "Yes. Google grants V-Kit analytics.readonly, so it can read reports and metadata and cannot change anything in GA4.",
      },
      {
        q: "Can I connect Google Analytics to ChatGPT?",
        a: "Yes. Install the V-Kit plugin from ChatGPT Plugins, on any plan, then authorize Google on Connections. The steps are on [Install](/install#chatgpt).",
      },
      faqStored("GA4"),
    ],
    related: ["/mcp/search-console", "/mcp/google-ads", "/mobile-app-marketing-mcp", "/vs/supermetrics"],
    breadcrumb: "Google Analytics 4",
  },

  // ── 6. Search Console ───────────────────────────────────────────────────────────────────────
  {
    path: "/mcp/search-console",
    title: "Google Search Console MCP for Claude | V-Kit",
    description:
      "Ask Claude, Claude Code or ChatGPT about Search Console queries, pages, clicks, impressions, CTR and position. Hosted, Google sign-in, read-only.",
    kicker: "Search Console · Claude · ChatGPT",
    h1: "Google Search Console MCP for Claude and ChatGPT",
    lead: "Organic Google Search in plain words: which queries and pages bring clicks, how impressions, CTR and average position change, and whether a URL is indexed.",
    sources: ["search_console"],
    connectAnchor: "search_console",
    sections: [
      {
        h2: "What you can ask",
        bullets: [
          "“Top 20 queries by clicks last month, with CTR and average position.”",
          "“Which pages lost the most clicks compared with the previous 28 days?”",
          "“Clicks and impressions by country and device for pages under /blog/.”",
          "“Queries where we rank between 5 and 15 with many impressions but a low CTR.”",
          "“Is this URL indexed, and which canonical did Google choose?” URL Inspection.",
          "“Do our sitemaps have errors or warnings?”",
        ],
      },
      {
        h2: "What a report covers",
        paragraphs: [
          "Clicks, impressions, CTR and average position by date, query, page, country and device, for web, image, video, news, Discover and Google News search, with filters. Up to 50,000 rows per report, with totals and an impression-weighted position. Results too large for the chat come as a download link valid for 24 hours, only for you.",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          STEP_INSTALL,
          "On [Connections](/connections#search_console) press Authorize with Google and keep the Search Console box checked. The same sign-in covers [GA4](/mcp/google-analytics-4) and [Google Ads](/mcp/google-ads).",
          STEP_ASK,
        ],
      },
      {
        h2: "What it reads",
        bullets: [
          "Scope webmasters.readonly: your properties, search performance, sitemaps and URL index status.",
          "It does not add, remove or change properties or sitemaps.",
          "You see the properties your Google account is a user of, both URL-prefix and domain properties.",
        ],
      },
      {
        h2: "Limits",
        bullets: [
          "Google publishes search data with a delay of 2–3 days, so the last days are empty.",
          "Dates are in Pacific time, as in Search Console.",
          "Connected Google before Search Console was added? Authorize Google again; GA4 and Google Ads keep working meanwhile.",
        ],
      },
    ],
    faq: [
      {
        q: "Is it read-only?",
        a: "Yes. Google grants V-Kit webmasters.readonly; it reads search performance, sitemaps and index status and changes nothing.",
      },
      {
        q: "Do I need to verify my site again?",
        a: "No. The Google account you sign in with must already be a user of the property in Search Console; V-Kit reads what that account can see.",
      },
      FAQ_CHATGPT,
      faqStored("Search Console"),
    ],
    related: ["/mcp/google-analytics-4", "/mcp/google-ads", "/vs/supermetrics"],
    breadcrumb: "Search Console",
  },

  // ── 7. Google Ads ───────────────────────────────────────────────────────────────────────────
  {
    path: "/mcp/google-ads",
    title: "Google Ads MCP: No Developer Token or GCP Setup | V-Kit",
    description:
      "Read Google Ads spend, campaigns and conversions from Claude, Claude Code or ChatGPT. Hosted, Google sign-in, no developer token or own Cloud project.",
    kicker: "Google Ads · Claude · ChatGPT",
    h1: "Google Ads MCP for Claude, without your own developer token",
    lead: "Spend, clicks, conversions and campaign settings from Google Ads, answered in plain words. V-Kit calls the Google Ads API through its own Google app; you only sign in with Google.",
    sources: ["google_ads"],
    connectAnchor: "google_ads",
    sections: [
      {
        h2: "What you can ask",
        bullets: [
          "“Spend, clicks and conversions by campaign last week.”",
          "“Which ad groups had the highest cost per conversion this month?”",
          "“List paused campaigns with their budgets.”",
          "“Which conversion actions are set up?”",
          "“Which Google Ads accounts do I have?” Direct ones and those under manager accounts, with name and status.",
        ],
      },
      {
        h2: "Nothing to set up in Google Cloud",
        paragraphs: [
          "You need no developer token and no Google Cloud project of your own. Google now ties API access levels to the Google Cloud project ([Google’s note](https://developers.google.com/google-ads/api/docs/developer-toolkit/mcp-server)); that project is ours. You authorize Google once on [Connections](/connections#google_ads), and reports go through GAQL, Google Ads’ own query language, so any report the API allows can be asked for.",
          "Manager accounts (MCC) are detected automatically: V-Kit finds every account you reach directly or through a manager and sends the right login customer ID itself. Only if an account is visible through several managers and you need a specific one, set a login customer ID under Advanced on Connections.",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          STEP_INSTALL,
          "On [Connections](/connections#google_ads) press Authorize with Google and keep every box checked. The same sign-in covers [GA4](/mcp/google-analytics-4) and [Search Console](/mcp/search-console).",
          STEP_ASK,
        ],
      },
      {
        h2: "What it reads",
        bullets: [
          "Google has no read-only permission for Google Ads, so the consent asks for adwords. V-Kit only runs reports and reference lists of campaigns, ad groups, ads and conversion actions; it does not create, change or pause anything.",
          "You see the accounts your Google account can reach, at any access level.",
          "Reports need a period and a row limit; large results come as a download link valid for 24 hours, only for you.",
        ],
      },
    ],
    faq: [
      {
        q: "Do I need a Google Ads developer token?",
        a: "No, and no Google Cloud project either: V-Kit calls the Google Ads API through its own Google app. Until Google approves our access level, production accounts may get an access error (see the status above).",
      },
      {
        q: "Does it work with a manager account (MCC)?",
        a: "Yes. Accounts under a manager are found automatically, also through several levels. A login customer ID under Advanced on Connections is only needed to pick one manager when an account is visible through several.",
      },
      {
        q: "Is it read-only?",
        a: "It only reads. The Google permission is the general adwords one, because Google offers no read-only option, but V-Kit has no tool that changes campaigns, bids or budgets.",
      },
      FAQ_CHATGPT,
      faqStored("Google Ads"),
    ],
    related: ["/mcp/google-analytics-4", "/mcp/search-console", "/mcp/meta-ads", "/mobile-app-marketing-mcp", "/vs/supermetrics"],
    breadcrumb: "Google Ads",
  },

  // ── 8. Meta Ads ─────────────────────────────────────────────────────────────────────────────
  {
    path: "/mcp/meta-ads",
    title: "Meta Ads MCP (Read-Only) for Claude & ChatGPT | V-Kit",
    description:
      "Analyze Facebook and Instagram ad spend, installs and campaigns in Claude, Claude Code or ChatGPT. Read-only (ads_read): the AI can’t change campaigns.",
    kicker: "Meta Ads · Claude · ChatGPT",
    h1: "Meta Ads MCP for Claude and ChatGPT, read-only",
    lead: "Spend, impressions, clicks, installs and conversions from your Meta ad accounts, answered in plain words. For ads, V-Kit asks Meta only for ads_read, so it cannot change a campaign.",
    sources: ["meta"],
    connectAnchor: "meta",
    sections: [
      {
        h2: "What you can ask",
        bullets: [
          "“Spend, CTR and cost per install by campaign last week.”",
          "“Break down yesterday’s spend by age, gender and placement.”",
          "“Compare purchases with 1-day click and 7-day click attribution.”",
          "“Which ad sets are active, and what are their objectives, budgets and targeting?”",
          "“How many app and pixel events did we get this week?”",
          "“Which custom conversions does this ad account have?”",
        ],
      },
      {
        h2: "Read-only by design",
        paragraphs: [
          "For ads, Facebook Login asks for ads_read. There is no tool that creates, edits, pauses or deletes campaigns, ad sets, ads or budgets, and the permission would not allow it.",
          "The same Meta connection also covers [Facebook Pages](/mcp/facebook-pages) and [Instagram](/mcp/instagram), each with its own read permissions.",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          STEP_INSTALL,
          "On [Connections](/connections#meta) press Authorize with Meta, keep every permission and pick your businesses and ad accounts.",
          STEP_ASK,
        ],
      },
      {
        h2: "Limits",
        bullets: [
          "You see the ad accounts where your Facebook account has a role.",
          "A Meta connection lasts about 60 days; then press Authorize again.",
          "Installs here are Meta’s own count; for attribution across channels compare with [AppsFlyer](/mcp/appsflyer).",
          "If Facebook says the app isn’t available, Meta’s review of the Marketing Kit app is still pending and only people with a role in the app can connect; write to connect@valas.team.",
        ],
      },
    ],
    faq: [
      {
        q: "Can the AI change my Meta campaigns?",
        a: "No. V-Kit has no tool for it, and the ads permission it asks for, ads_read, only allows reading.",
      },
      {
        q: "Are Instagram ads included?",
        a: "Yes. Instagram ads run through Meta Ads Manager and are in the same reports; break them down by placement. Organic Instagram is the [Instagram](/mcp/instagram) source.",
      },
      FAQ_CHATGPT,
      faqStored("Meta Ads"),
    ],
    related: ["/mcp/instagram", "/mcp/facebook-pages", "/mcp/tiktok-ads", "/mobile-app-marketing-mcp", "/vs/supermetrics"],
    breadcrumb: "Meta Ads",
  },

  // ── 9. TikTok Ads ───────────────────────────────────────────────────────────────────────────
  {
    path: "/mcp/tiktok-ads",
    title: "TikTok Ads MCP for Claude & ChatGPT | V-Kit",
    description:
      "Ask Claude, Claude Code or ChatGPT about TikTok Ads spend, impressions, clicks and conversions by campaign, ad group and ad. Read-only.",
    kicker: "TikTok Ads · Claude · ChatGPT",
    h1: "TikTok Ads MCP for Claude and ChatGPT",
    lead: "Spend, impressions, clicks and conversions from your TikTok advertiser accounts, by campaign, ad group and ad, answered in plain words.",
    sources: ["tiktok"],
    connectAnchor: "tiktok",
    sections: [
      {
        h2: "What you can ask",
        bullets: [
          "“Spend and conversions by campaign last week, by day.”",
          "“Which ads had the lowest cost per conversion this month?”",
          "“List active ad groups with their budgets.”",
          "“Compare TikTok spend with [Meta](/mcp/meta-ads) and [Google Ads](/mcp/google-ads) for the same days.”",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          STEP_INSTALL,
          "On [Connections](/connections#tiktok) press Authorize with TikTok, sign in to TikTok for Business and allow the advertiser accounts you need.",
          STEP_ASK,
        ],
      },
      {
        h2: "What it reads",
        bullets: [
          "Advertisers, ad performance reports and campaigns, ad groups and ads with status, objective and budget. Read-only.",
          "Long periods are split into 30-day windows automatically.",
          "The token is stored encrypted on the server; disconnecting deletes it and asks TikTok to revoke it.",
        ],
      },
      {
        h2: "Limits",
        bullets: [
          "Organic TikTok (videos, followers) is not covered.",
        ],
      },
    ],
    faq: [
      {
        q: "Is it read-only?",
        a: "Yes. V-Kit reads reports and campaign objects and has no tool that changes TikTok campaigns, ad groups or ads.",
      },
      {
        q: "Does it cover organic TikTok?",
        a: "No, only TikTok Ads. Organic videos and followers need a separate TikTok authorization that V-Kit does not have.",
      },
      FAQ_CHATGPT,
      faqStored("TikTok"),
    ],
    related: ["/mcp/meta-ads", "/mcp/google-ads", "/mcp/apple-ads", "/mobile-app-marketing-mcp", "/vs/supermetrics"],
    breadcrumb: "TikTok Ads",
  },

  // ── 9b. Apple Ads (BL-200; facts: ADR-0065, docs/research/2026-10-15-apple-ads-api.md, packs/apple-ads) ──
  {
    path: "/mcp/apple-ads",
    title: "Apple Ads MCP for Claude & ChatGPT | V-Kit",
    description:
      "Ask Claude, Claude Code or ChatGPT about Apple Ads: spend, taps, installs, CPT and CPI by campaign, keyword and search term. Read-only, Apple sign-in.",
    kicker: "Apple Ads · Claude · ChatGPT",
    h1: "Apple Ads MCP for Claude and ChatGPT",
    lead: "Spend, impressions, taps and installs of your App Store search ads, by campaign, ad group, keyword, search term and ad, answered in plain words. You connect with an Apple sign-in; there are no keys to make.",
    sources: ["apple_ads"],
    connectAnchor: "apple_ads",
    sections: [
      {
        h2: "What you can ask",
        bullets: [
          "“Apple Ads spend, taps and installs by campaign last week.” CPT and CPI come with them.",
          "“Which keywords of this campaign had the highest cost per install this month?”",
          "“Which search terms brought installs in the US campaign?”",
          "“Spend by day over the last 30 days, split by country.” Also by device, and for campaigns and ad groups by age and gender.",
          "“What is our impression share for our top search terms in the US?”",
          "“Compare Apple Ads installs with [AppsFlyer](/mcp/appsflyer) and [App Store Connect](/mcp/app-store-connect) for the same days.”",
        ],
      },
      {
        h2: "Connected with Apple, not with a key",
        paragraphs: [
          "V-Kit connects to Apple Ads as a service provider, the way attribution and reporting tools do. An Account Admin or Campaign Group Manager of your Apple Ads account presses Connect Apple Ads on [Connections](/connections#apple-ads), signs in with Apple, chooses the ad account and the access and allows it. No API user, no certificate and nothing to paste.",
          "Apple Ads then lists V-Kit under Account Settings → API, with who granted the access, the account and the role; Revoke Access there withdraws it at any time.",
          "The connection works wherever you use V-Kit: the Claude plugin, the custom connector on claude.ai, Claude Code and ChatGPT.",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          STEP_INSTALL,
          "On [Connections](/connections#apple-ads) press Connect Apple Ads and sign in with the Apple Account that is an Account Admin or Campaign Group Manager in Apple Ads.",
          "Choose the ad account and read-only access, and allow. The row shows the organization and the ad accounts V-Kit can read.",
          "Ask: “Apple Ads spend last week by campaign”.",
        ],
      },
      {
        h2: "What it reads",
        bullets: [
          "The ad accounts of the grant, campaigns, ad groups, keywords, negative keywords and ads with their status, budgets and bids.",
          "Performance reports by campaign, ad group, keyword, search term or ad: spend, impressions, taps, installs, CPT, CPI and more, for the whole period or by day, week or month.",
          "Impression share of an app by search term and country, with rank and search popularity.",
          "Read-only: V-Kit never creates, changes, pauses or deletes anything in Apple Ads. The access is stored encrypted on the server; Disconnect deletes it.",
        ],
      },
      {
        h2: "Limits",
        bullets: [
          "Installs are counted by Apple Ads attribution, tap-through and view-through on Apple’s side; [AppsFlyer](/mcp/appsflyer) and [App Store Connect](/mcp/app-store-connect) count their own way.",
          "Amounts are in each ad account’s currency; accounts with different currencies are never added together.",
          "Daily reports cover up to 90 days at a time; weekly need at least 14 days, monthly at least 90. Search terms with little volume are hidden by Apple.",
          "Impression share needs the app and covers up to 30 days, or up to 4 weeks by week.",
          "App Store campaigns only; Apple Maps ad accounts are not covered.",
        ],
      },
    ],
    faq: [
      {
        q: "Do I need to create an Apple Ads API key?",
        a: "No. You sign in with Apple on [Connections](/connections#apple-ads) and allow V-Kit access; there is no API user, key or certificate to make. The Apple Account must be an Account Admin or Campaign Group Manager in Apple Ads.",
      },
      {
        q: "Can it change my campaigns?",
        a: "No. Every Apple Ads tool only reads. It does not create, pause or change campaigns, ad groups, keywords, bids or budgets.",
      },
      {
        q: "How do I take the access back?",
        a: "Press Disconnect Apple Ads on [Connections](/connections#apple-ads) to delete the connection in V-Kit, and in Apple Ads open Account Settings → API → Manage API Access and press Revoke Access on the V-Kit row.",
      },
      FAQ_CHATGPT,
      faqStored("Apple Ads"),
    ],
    related: ["/mcp/app-store-connect", "/mcp/appsflyer", "/mcp/tiktok-ads", "/mcp/meta-ads", "/mobile-app-marketing-mcp"],
    breadcrumb: "Apple Ads",
  },

  // ── 10. Instagram ───────────────────────────────────────────────────────────────────────────
  {
    path: "/mcp/instagram",
    title: "Instagram MCP for Claude: Reach, Posts, Reels | V-Kit",
    description:
      "Ask Claude, Claude Code or ChatGPT about your Instagram professional account: reach, views, follows, posts and reels insights. Read-only, Meta sign-in.",
    kicker: "Instagram · Claude · ChatGPT",
    h1: "Instagram MCP for Claude and ChatGPT",
    lead: "Reach, views, interactions and follows of your Instagram professional account, and insights for each post and reel, answered in plain words.",
    sources: ["instagram"],
    connectAnchor: "instagram",
    sections: [
      {
        h2: "What you can ask",
        bullets: [
          "“Reach and new follows by day for the last 30 days.”",
          "“Which reels from last month had the most views, saves and shares?”",
          "“Posts published this week with their likes and comments.”",
          "“Compare this month’s reach with last month’s.”",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          STEP_INSTALL,
          "Make sure the Instagram account is a Business or Creator account linked to a Facebook Page you manage. On [Connections](/connections#instagram) press Authorize with Meta and select that Page and the Instagram account.",
          STEP_ASK,
        ],
      },
      {
        h2: "What it reads",
        bullets: [
          "Permissions instagram_basic, instagram_manage_insights, pages_show_list and pages_read_engagement: profile, account insights, media and media insights. No posting, commenting or messages.",
          "Account insights in windows of up to 30 days, with an optional daily series.",
          "Metrics Meta does not support for a media type are reported as such, not guessed.",
          "Optional business_management adds the Instagram accounts of your business portfolio.",
        ],
      },
    ],
    faq: [
      {
        q: "Does it work with a personal Instagram account?",
        a: "No. Meta gives insights only for professional (Business or Creator) accounts linked to a Facebook Page.",
      },
      {
        q: "Is it read-only?",
        a: "Yes. V-Kit reads the profile, insights and media; it cannot post, comment or send messages.",
      },
      FAQ_CHATGPT,
      faqStored("Instagram"),
    ],
    related: ["/mcp/facebook-pages", "/mcp/meta-ads", "/vs/supermetrics"],
    breadcrumb: "Instagram",
  },

  // ── 11. Facebook Pages ──────────────────────────────────────────────────────────────────────
  {
    path: "/mcp/facebook-pages",
    title: "Facebook Pages MCP for Claude & ChatGPT | V-Kit",
    description:
      "Ask Claude, Claude Code or ChatGPT about your Facebook Pages: reach, content views, engagement, followers, post insights. Read-only, Meta sign-in.",
    kicker: "Facebook Pages · Claude · ChatGPT",
    h1: "Facebook Pages MCP for Claude and ChatGPT",
    lead: "Reach, content views, engagement and followers of the Facebook Pages you manage, and insights for each post, answered in plain words.",
    sources: ["fb_pages"],
    connectAnchor: "fb_pages",
    sections: [
      {
        h2: "What you can ask",
        bullets: [
          "“Page reach and followers by day this quarter.”",
          "“Top posts last month by reactions, comments and shares.”",
          "“Reach and clicks of these five posts.”",
          "“Which Pages can I see, including those of our business portfolio?”",
        ],
      },
      {
        h2: "How to connect",
        steps: [
          STEP_INSTALL,
          "On [Connections](/connections#fb_pages) press Authorize with Meta and choose the Pages to share. Already connected for Meta Ads? Authorize again to add the Page permissions; ads keep working.",
          STEP_ASK,
        ],
      },
      {
        h2: "What it reads",
        bullets: [
          "Permissions pages_show_list, pages_read_engagement and read_insights: Pages, posts, page and post insights. No posting or replying.",
          "Periods over 90 days are split automatically.",
          "Page tokens are derived inside a request and never stored or returned.",
          "Optional business_management adds the Pages of your business portfolio.",
          "Optional pages_read_user_content adds the number of reactions and comments on posts; their texts and authors are not requested. Without it, posts come back without those counts.",
        ],
      },
    ],
    faq: [
      {
        q: "Is it read-only?",
        a: "Yes. V-Kit reads Pages, posts and insights; it cannot post, reply or change Page settings.",
      },
      {
        q: "A Page is missing. Why?",
        a: "It was not selected in the Facebook dialog, or your Facebook account has no role on it. Authorize Meta again on [Connections](/connections#fb_pages) and select it.",
      },
      FAQ_CHATGPT,
      faqStored("Facebook Pages"),
    ],
    related: ["/mcp/instagram", "/mcp/meta-ads", "/vs/supermetrics"],
    breadcrumb: "Facebook Pages",
  },

  // ── 12. vs Others: the hub of comparisons (facts from the /vs/<slug> pages, BL-111) ─────────
  {
    path: "/vs",
    title: "V-Kit vs Supermetrics MCP and Windsor.ai",
    description:
      "V-Kit, Supermetrics MCP and Windsor.ai compared for Claude and ChatGPT: sources, sign-in, write access and mobile app data, checked 2026-10-04.",
    kicker: "Comparison · Claude · ChatGPT",
    h1: "V-Kit and other ways to connect marketing data to Claude and ChatGPT",
    lead: "Supermetrics MCP, Windsor.ai and V-Kit each connect marketing data to Claude and ChatGPT over MCP. Below is what each one covers, from public pages checked on 2026-10-04, with a page for each comparison.",
    sources: [],
    checked: "2026-10-04",
    sections: [
      {
        h2: "What is compared",
        paragraphs: [
          "Each comparison is built from the other service’s public pages, with links to them and the date they were checked. Supermetrics and Windsor.ai prices are not repeated here; their pricing pages are linked below.",
        ],
      },
      {
        h2: "Supermetrics MCP",
        paragraphs: [
          "Supermetrics MCP is a hosted connector on mcp.supermetrics.com for Claude, ChatGPT, Copilot, Gemini Enterprise and other MCP clients, with OAuth 2.0 per user. Its data source list has 175 entries, including AppsFlyer, Adjust, Branch, Apple Search Ads and Google Play Console; RevenueCat and App Store Connect are not in it. Its documentation lists, in beta, a tool that creates or updates ad campaigns. [V-Kit vs Supermetrics MCP](/vs/supermetrics)",
        ],
      },
      {
        h2: "Windsor.ai",
        paragraphs: [
          "Windsor.ai runs a hosted MCP server at https://mcp.windsor.ai/ for Claude, ChatGPT, Copilot and other MCP clients; you sign in with a Windsor.ai account and connect data sources in Windsor.ai first. Its connectors page lists 350+ sources, including AppsFlyer, Adjust, Branch and Apple Search Ads; RevenueCat, App Store Connect and Google Play Console are not in it. Its documentation describes write-back actions on select platforms such as Meta Ads, Google Ads, TikTok Ads, LinkedIn Ads, Microsoft/Bing Ads and Amazon Seller Central. [V-Kit vs Windsor.ai](/vs/windsor-ai)",
        ],
      },
      {
        h2: "When V-Kit fits",
        paragraphs: [
          "When your questions are about a mobile app: installs from [AppsFlyer](/mcp/appsflyer), subscriptions from [RevenueCat](/mcp/revenuecat), sales, analytics and reviews from [App Store Connect](/mcp/app-store-connect), Android installs, sales and reviews from [Google Play](/mcp/google-play), next to spend from Meta, Google Ads, TikTok and [Apple Ads](/mcp/apple-ads) - see [V-Kit for mobile app marketing](/mobile-app-marketing-mcp). V-Kit reads ad platforms and changes no campaigns. Both services above list platforms that V-Kit does not connect, such as LinkedIn Ads.",
          `Agencies and freelancers keep up to ${CLIENTS_WORD} customers’ sign-ins and keys apart in one V-Kit account, in the same price - see [V-Kit for agencies](/for-agencies).`,
        ],
      },
      {
        h2: "Where the facts come from",
        paragraphs: [
          "Supermetrics: [the Supermetrics MCP page](https://supermetrics.com/products/supermetrics-mcp), [the MCP documentation](https://mcp.supermetrics.com/) and [the MCP data source list](https://mcp.supermetrics.com/datasources); prices on [their pricing page](https://supermetrics.com/pricing).",
          "Windsor.ai: [connecting Windsor MCP to any AI client](https://windsor.ai/documentation/windsor-mcp/how-to-connect-windsor-mcp-to-any-ai-client/), [their connectors page](https://windsor.ai/connectors/) and [their security page](https://windsor.ai/security/); prices on [their pricing page](https://windsor.ai/pricing/).",
          "All checked on 2026-10-04. If anything has changed since, their pages win. V-Kit: this site, the [privacy policy](/privacy.html) and the [terms](/terms.html).",
        ],
      },
    ],
    table: {
      caption: "From public pages, checked on 2026-10-04",
      head: ["", "V-Kit", "Supermetrics MCP", "Windsor.ai"],
      rows: [
        ["Hosted MCP connector", "Yes: https://marketing-kit.app/mcp", "Yes: endpoint on mcp.supermetrics.com", "Yes: https://mcp.windsor.ai/"],
        ["Sign-in", "OAuth; you sign in with Google or Meta", "OAuth 2.0 per user; API keys for server-to-server", "Windsor.ai account; data sources connected in Windsor.ai first"],
        ["Assistants", "Claude (plugin, custom connector, Claude Code) and ChatGPT", "Claude, ChatGPT, Copilot, Gemini Enterprise and other MCP clients", "Claude, ChatGPT, Copilot, Claude Code, Cursor, Perplexity and other MCP clients"],
        ["Sources", "13: Meta Ads, Facebook Pages, Instagram, TikTok Ads, Apple Ads, Google Ads, GA4, Search Console, AppsFlyer, RevenueCat, App Store Connect, Google Play, App Store (public)", "175 in their MCP data source list", "350+ on their connectors page"],
        ["Mobile app sources", "Apple Ads (App Store search ads), AppsFlyer, RevenueCat, App Store Connect (sales, subscriptions, analytics, reviews), Google Play (installs, sales, earnings, subscriptions, reviews, refunds, vitals), public App Store data", "AppsFlyer, Adjust, Branch, Apple Public Data, Apple Search Ads, Google Play Console; RevenueCat and App Store Connect not in the list", "AppsFlyer, Adjust, Branch, Apple Search Ads; RevenueCat, App Store Connect and Google Play Console not in the list"],
        ["Changes", "Reads; two kinds of change after your explicit yes (AppsFlyer partner-integration copy, App Store Connect Analytics Reports)", "Data tools, plus a beta tool that creates or updates ad campaigns", "Read access, plus write-back actions on select ad platforms and Amazon Seller Central"],
        ["Several clients", CLIENTS_ROW_VKIT, CLIENTS_ROW_NOT_STATED, CLIENTS_ROW_NOT_STATED],
        ["Price", priceText("row"), "See their pricing page", "See their pricing page"],
      ],
    },
    faq: [
      {
        q: "Which one has RevenueCat and App Store Connect?",
        a: "V-Kit connects both. On 2026-10-04 neither was in the Supermetrics MCP data source list or on the Windsor.ai connectors page.",
      },
      {
        q: priceText("faqQ"),
        a: priceText("faqA"),
      },
      {
        q: "Which of them can change ad campaigns?",
        a: "Supermetrics MCP lists a beta tool that creates or updates ad campaigns, and Windsor.ai describes write-back actions such as pausing or launching campaigns and reallocating budgets. V-Kit has no tool that changes campaigns.",
      },
    ],
    related: ["/vs/supermetrics", "/vs/windsor-ai", "/mobile-app-marketing-mcp", "/for-agencies", "/mcp/appsflyer"],
    breadcrumb: "vs Others",
    footnote:
      "Supermetrics is a trademark of Supermetrics Oy. Windsor.ai is a trademark of Windsor Group AG. V-Kit is not affiliated with or endorsed by either company.",
  },

  // ── 13. vs Supermetrics (facts checked 2026-10-04, URLs in the text) ──────────────────────
  {
    path: "/vs/supermetrics",
    title: "Supermetrics MCP Alternative for Claude | V-Kit",
    description:
      "V-Kit and Supermetrics MCP compared for Claude and ChatGPT: sources, write access, mobile app data and price, from public pages checked 2026-10-04.",
    kicker: "Comparison · Claude · ChatGPT",
    h1: "V-Kit vs Supermetrics MCP",
    lead: "Both connect marketing data to Claude and ChatGPT over MCP. Supermetrics covers a wide catalog of platforms; V-Kit covers fewer sources, with mobile app data and reading by default.",
    sources: [],
    checked: "2026-10-04",
    sections: [
      {
        h2: "In short",
        bullets: [
          "Supermetrics MCP fits if you need many platforms: its data source list has 175 entries, from LinkedIn Ads to Shopify and HubSpot.",
          "V-Kit fits if your questions are about a mobile app: spend from Meta, Google Ads, TikTok and [Apple Ads](/mcp/apple-ads), installs from [AppsFlyer](/mcp/appsflyer), subscriptions from [RevenueCat](/mcp/revenuecat), sales and analytics from [App Store Connect](/mcp/app-store-connect), Android installs and sales from [Google Play](/mcp/google-play).",
        ],
      },
      {
        h2: "Changing things",
        paragraphs: [
          "Supermetrics’ MCP documentation lists, in beta, a tool that creates or updates ad campaigns across Google, Meta, Microsoft, TikTok, LinkedIn, ChatGPT and Snapchat Ads. V-Kit has no tool that changes campaigns: it reads ad platforms, and its only changes - copying AppsFlyer partner integration settings, and turning on App Store Connect Analytics Reports - run after your explicit yes.",
        ],
      },
      { h2: "Your data on our side", bullets: PRIVACY_BULLETS },
      {
        h2: "Where the facts come from",
        paragraphs: [
          "Supermetrics: their public pages on 2026-10-04 - [the Supermetrics MCP page](https://supermetrics.com/products/supermetrics-mcp), [the MCP documentation](https://mcp.supermetrics.com/) and [the MCP data source list](https://mcp.supermetrics.com/datasources). For prices see [their pricing page](https://supermetrics.com/pricing). If anything has changed since, their pages win.",
          "V-Kit: this site, the [privacy policy](/privacy.html) and the [terms](/terms.html).",
        ],
      },
    ],
    table: {
      caption: "From public pages, checked on 2026-10-04",
      head: ["", "V-Kit", "Supermetrics MCP"],
      rows: [
        ["Hosted MCP connector", "Yes: https://marketing-kit.app/mcp", "Yes: endpoint on mcp.supermetrics.com"],
        ["Sign-in", "OAuth; you sign in with Google or Meta", "OAuth 2.0 per user; API keys for server-to-server"],
        ["Assistants", "Claude (plugin, custom connector, Claude Code) and ChatGPT", "Claude, ChatGPT, Copilot, Gemini Enterprise and other MCP clients"],
        ["Sources", "13: Meta Ads, Facebook Pages, Instagram, TikTok Ads, Apple Ads, Google Ads, GA4, Search Console, AppsFlyer, RevenueCat, App Store Connect, Google Play, App Store (public)", "175 in their MCP data source list"],
        ["Mobile app sources", "Apple Ads (App Store search ads), AppsFlyer, RevenueCat, App Store Connect (sales, subscriptions, analytics, reviews), Google Play (installs, sales, earnings, subscriptions, reviews, refunds, vitals), public App Store data", "AppsFlyer, Adjust, Branch, Apple Public Data, Apple Search Ads, Google Play Console; RevenueCat and App Store Connect not in the list"],
        ["Changes", "Reads; two kinds of change after your explicit yes (AppsFlyer partner-integration copy, App Store Connect Analytics Reports)", "Data tools, plus a beta tool that creates or updates ad campaigns"],
        ["Several clients", CLIENTS_ROW_VKIT, CLIENTS_ROW_NOT_STATED],
        ["Price", priceText("row"), "Included in plans with Data API access; 14-day free trial, no credit card"],
      ],
    },
    faq: [
      {
        q: priceText("faqQ"),
        a: priceText("faqA"),
      },
      {
        q: "Does Supermetrics MCP support AppsFlyer and App Store data?",
        a: "Their MCP data source list on 2026-10-04 had AppsFlyer, Apple Public Data and Apple Search Ads; RevenueCat and App Store Connect were not in it.",
      },
      {
        q: "Is V-Kit read-only?",
        a: "Every tool reads, except two that run only after your explicit yes: copying AppsFlyer partner integration settings and turning on App Store Connect Analytics Reports. Nothing changes ad campaigns.",
      },
      FAQ_CHATGPT,
      faqStored("marketing"),
    ],
    related: ["/vs", "/vs/windsor-ai", "/mobile-app-marketing-mcp", "/mcp/appsflyer", "/mcp/app-store-connect", "/mcp/google-ads", "/mcp/meta-ads"],
    breadcrumb: "V-Kit vs Supermetrics",
    parent: "/vs",
    footnote: "Supermetrics is a trademark of Supermetrics Oy. V-Kit is not affiliated with or endorsed by Supermetrics.",
  },

  // ── 14. vs Windsor.ai (facts checked 2026-10-04, URLs in the text, BL-111) ────────────────
  {
    path: "/vs/windsor-ai",
    title: "Windsor.ai MCP Alternative for Claude | V-Kit",
    description:
      "V-Kit and Windsor.ai compared for Claude and ChatGPT: sources, write access, mobile app data, data retention, from public pages checked 2026-10-04.",
    kicker: "Comparison · Claude · ChatGPT",
    h1: "V-Kit vs Windsor.ai",
    lead: "Both connect marketing data to Claude and ChatGPT over MCP. Windsor.ai covers a wide catalog of platforms; V-Kit covers fewer sources, with mobile app data and reading by default.",
    sources: [],
    checked: "2026-10-04",
    sections: [
      {
        h2: "In short",
        bullets: [
          "Windsor.ai fits if you need many platforms: its connectors page lists 350+ sources, and its MCP server works with Claude, ChatGPT, Copilot, Cursor, Perplexity, Claude Code and other MCP clients.",
          "V-Kit fits if your questions are about a mobile app: spend from Meta, Google Ads, TikTok and [Apple Ads](/mcp/apple-ads), installs from [AppsFlyer](/mcp/appsflyer), subscriptions from [RevenueCat](/mcp/revenuecat), sales and analytics from [App Store Connect](/mcp/app-store-connect), Android installs and sales from [Google Play](/mcp/google-play).",
        ],
      },
      {
        h2: "Changing things",
        paragraphs: [
          "Windsor.ai’s MCP documentation says that for Claude, ChatGPT and Microsoft Copilot it supports read access and write-back actions on select platforms like Meta Ads, Google Ads, TikTok Ads, LinkedIn Ads, Microsoft/Bing Ads and Amazon Seller Central; its examples include pausing or launching campaigns, reallocating budgets and pushing negative keywords. V-Kit has no tool that changes campaigns: it reads ad platforms, and its two kinds of change - copying AppsFlyer partner integration settings, and turning on App Store Connect Analytics Reports - run after your explicit yes.",
        ],
      },
      {
        h2: "Data on their side and on ours",
        paragraphs: [
          "Windsor.ai’s [security page](https://windsor.ai/security/) says that customer-processed data exists as cached data in their systems: data held in memory in connectors is kept for up to 24 hours, data cached to disk is deleted if it has not been accessed for at least 20 days, and the maximum retention period is about three weeks. The same page says they retain customer access tokens, stored securely and encrypted.",
          "V-Kit keeps your numbers in a 15-minute in-memory cache, and a result too large for the chat waits behind a download link for up to 24 hours. Tokens and keys are encrypted at rest (AES-256-GCM).",
        ],
      },
      { h2: "Your data on our side", bullets: PRIVACY_BULLETS },
      {
        h2: "Where the facts come from",
        paragraphs: [
          "Windsor.ai: their public pages on 2026-10-04 - [connecting Windsor MCP to any AI client](https://windsor.ai/documentation/windsor-mcp/how-to-connect-windsor-mcp-to-any-ai-client/), [integrating data into Claude](https://windsor.ai/documentation/windsor-mcp/how-to-integrate-data-into-claude/), [integrating data into ChatGPT](https://windsor.ai/documentation/windsor-mcp/how-to-integrate-data-into-chatgpt/), [their connectors page](https://windsor.ai/connectors/), [connecting AppsFlyer to Claude](https://windsor.ai/how-to-connect-appsflyer-to-claude/), [their security page](https://windsor.ai/security/) and [their privacy policy](https://windsor.ai/privacy-policy/). For prices see [their pricing page](https://windsor.ai/pricing/). If anything has changed since, their pages win.",
          "V-Kit: this site, the [privacy policy](/privacy.html) and the [terms](/terms.html).",
        ],
      },
    ],
    table: {
      caption: "From public pages, checked on 2026-10-04",
      head: ["", "V-Kit", "Windsor.ai"],
      rows: [
        ["Hosted MCP connector", "Yes: https://marketing-kit.app/mcp", "Yes: https://mcp.windsor.ai/"],
        ["Sign-in", "OAuth; you sign in with Google or Meta", "In Claude, sign in with a Windsor.ai account when adding the connector; in ChatGPT, Allow Access; an optional API key for other MCP clients; data sources are connected in Windsor.ai first"],
        ["Assistants", "Claude (plugin, custom connector, Claude Code) and ChatGPT", "Claude, ChatGPT and Copilot; also Cursor, Perplexity, Claude Code, Cline and other MCP clients"],
        ["Sources", "13: Meta Ads, Facebook Pages, Instagram, TikTok Ads, Apple Ads, Google Ads, GA4, Search Console, AppsFlyer, RevenueCat, App Store Connect, Google Play, App Store (public)", "350+ on their connectors page"],
        ["Mobile app sources", "Apple Ads (App Store search ads), AppsFlyer, RevenueCat, App Store Connect (sales, subscriptions, analytics, reviews), Google Play (installs, sales, earnings, subscriptions, reviews, refunds, vitals), public App Store data", "AppsFlyer, Adjust, Branch, Apple Search Ads; RevenueCat, App Store Connect and Google Play Console not in their connector list"],
        ["Changes", "Reads; two kinds of change after your explicit yes (AppsFlyer partner-integration copy, App Store Connect Analytics Reports)", "Read access, plus write-back actions on select platforms (Meta Ads, Google Ads, TikTok Ads, LinkedIn Ads, Microsoft/Bing Ads, Amazon Seller Central)"],
        ["Data retention", "15-minute in-memory cache; large results behind a download link for up to 24 hours; tokens and keys encrypted (AES-256-GCM)", "In memory up to 24 hours; disk cache deleted after 20 days without access, about three weeks at most; access tokens retained encrypted"],
        ["Several clients", CLIENTS_ROW_VKIT, CLIENTS_ROW_NOT_STATED],
        ["Price", priceText("row"), "See their pricing page"],
      ],
    },
    faq: [
      {
        q: priceText("faqQ"),
        a: priceText("faqA"),
      },
      {
        q: "Does Windsor.ai support AppsFlyer, RevenueCat and App Store Connect?",
        a: "Their [connectors page](https://windsor.ai/connectors/) on 2026-10-04 had AppsFlyer, Adjust, Branch and Apple Search Ads; RevenueCat, App Store Connect and Google Play Console were not in it.",
      },
      {
        q: "Is V-Kit read-only?",
        a: "Every tool reads, except two that run only after your explicit yes: copying AppsFlyer partner integration settings and turning on App Store Connect Analytics Reports. Nothing changes ad campaigns.",
      },
      FAQ_CHATGPT,
      faqStored("marketing"),
    ],
    related: ["/vs", "/vs/supermetrics", "/mobile-app-marketing-mcp", "/mcp/appsflyer", "/mcp/revenuecat"],
    breadcrumb: "V-Kit vs Windsor.ai",
    parent: "/vs",
    footnote: "Windsor.ai is a trademark of Windsor Group AG. V-Kit is not affiliated with or endorsed by Windsor.ai.",
  },
];

// ── «Works in Claude Code» (BL-154): the same on every source page and on the mobile-stack page ──
// Command and path are the ones of /install («Other ways for Claude») and /guide#claude-code
// (PLUGIN_INSTALL_COMMAND, MARKETPLACE_ADD_COMMAND, install.code); a test compares them with those constants.

const claudeCodeSection = (page: LandingPage): LandingSection => ({
  h2: "Works in Claude Code",
  paragraphs: [
    "Claude Code uses the same V-Kit connector. Run claude plugin marketplace add arey325/marketing-kit-plugin, then claude plugin install v-marketing-kit@v-marketing-kit. After /reload-plugins, open /mcp → v-marketing-kit → authenticate.",
    `${
      page.connectAnchor
        ? `Then connect ${page.breadcrumb} on [Connections](/connections#${page.connectAnchor})`
        : "Then connect your sources on [Connections](/connections)"
    } and ask in Claude Code. The steps are in the [Setup guide](/guide#claude-code).`,
  ],
});

/** Puts the Claude Code section right after «How to connect» on /mcp/<source> and /mobile-app-marketing-mcp. */
function withClaudeCode(page: LandingPage): LandingPage {
  if (!page.path.startsWith("/mcp/") && page.path !== "/mobile-app-marketing-mcp") return page;
  const at = page.sections.findIndex((s) => s.h2 === "How to connect");
  if (at < 0) throw new Error(`${page.path}: no «How to connect» section for the Claude Code block`);
  const sections = [...page.sections];
  sections.splice(at + 1, 0, claudeCodeSection(page));
  return { ...page, sections };
}

export const LANDING_PAGES: LandingPage[] = PAGES.map(withClaudeCode);

export interface PublicRoute {
  path: string;
  title: string;
  description: string;
  /** Name in the BreadcrumbList (Home → name); landing pages carry their own. */
  breadcrumb?: string;
  /** Built but unlisted: `noindex`, not in sitemap.xml (MK_PRICING_PUBLIC=off, ADR-0048). */
  unlisted?: boolean;
  /** Questions for the FAQPage JSON-LD of a page that is not a landing page. */
  faq?: LandingFaq[];
}

/** The /pricing questions (the mockup «Pricing»): the page and its FAQPage JSON-LD. */
export const PRICING_FAQ: LandingFaq[] = [1, "Clients", 2, 3, 4, 5, 6].map((n) => ({
  q: t(`pricing.faq${n}.q` as MessageKey),
  a: t(`pricing.faq${n}.a` as MessageKey, { count: CLIENTS_WORD }),
}));

/** The /pricing question about clients (ADR-0062, BL-194): the page adds a link to it - /clients/more or /support. */
export const PRICING_FAQ_CLIENTS_Q = t("pricing.faqClients.q");

export const PUBLIC_ROUTES: PublicRoute[] = [
  { path: "/", title: t("meta.title"), description: t("meta.description") },
  { path: "/install", title: t("meta.title.install"), description: t("meta.description.install") },
  { path: "/guide", title: t("meta.title.guide"), description: t("meta.description.guide"), breadcrumb: t("guidePage.h1") },
  { path: "/support", title: t("meta.title.support"), description: t("meta.description.support"), breadcrumb: t("support.h1") },
  {
    path: "/pricing",
    title: t("pricing.metaTitle"),
    description: t("pricing.metaDescription"),
    breadcrumb: t("pricing.kicker"),
    faq: PRICING_FAQ,
    unlisted: !PRICING_PUBLIC,
  },
  ...LANDING_PAGES.map(({ path, title, description, breadcrumb }) => ({ path, title, description, breadcrumb })),
];

export function publicRoute(path: string): PublicRoute | undefined {
  return PUBLIC_ROUTES.find((r) => r.path === path);
}

export function landingByPath(path: string): LandingPage | undefined {
  return LANDING_PAGES.find((p) => p.path === path);
}

/** The landing page of a source (for the «More about …» link in its modal), if there is one. */
export function landingFor(sourceId: string): LandingPage | undefined {
  return LANDING_PAGES.find((p) => p.connectAnchor === sourceId);
}
